"use client";

import {
  Button,
  Card,
  Heading,
  Inline,
  Stack,
  Text,
} from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { PolicyStateFeedback, type PolicyResourceState } from "../policy-state";
import type { PolicyVersionProgressCheck } from "./policy-progress-records";
import { PolicyProgressCheck } from "./policy-progress-check";

export type PolicyVersionProgressState = PolicyResourceState<{
  readonly checks: readonly PolicyVersionProgressCheck[];
}>;
export type PolicyVersionProgressProps = {
  readonly state: PolicyVersionProgressState;
  readonly onViewAll?: (() => void) | undefined;
  readonly onOpenCheck?: ((id: string) => void) | undefined;
  readonly onRetry?: () => void;
};
export function PolicyVersionProgress({
  state,
  onViewAll,
  onOpenCheck,
  onRetry,
}: PolicyVersionProgressProps) {
  return (
    <Card>
      <Stack width="auto" gap={16}>
        <Inline justify="between">
          <Heading level={2}>Progress</Heading>
          <Inline gap={8}>
            <PolicyStateFeedback
              state={state}
              subject="progress checks"
              onRetry={onRetry}
            />
            {onViewAll && (
              <Button size="sm" variant="quiet" onClick={onViewAll}>
                View all
              </Button>
            )}
          </Inline>
        </Inline>
        {state.status === "ready" &&
          (state.checks.length === 0 ? (
            <Text tone="muted">
              This version has not been measured against its predecessor yet.
              Upload a new version after league play, or start a check via the
              API.
            </Text>
          ) : (
            <ul {...stylex.props(styles.list)}>
              {state.checks.map((check) => (
                <PolicyProgressCheck
                  key={check.id}
                  check={check}
                  onOpenCheck={onOpenCheck}
                />
              ))}
            </ul>
          ))}
      </Stack>
    </Card>
  );
}

const styles = stylex.create({
  list: { listStyle: "none", margin: 0, padding: 0 },
});
